"use client";

import { Box, Flex, Heading, Steps, Text } from "@chakra-ui/react";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import TravelLandingView from "@features/services/travel/TravelLandingView";
import TravelPageLayout from "@features/services/travel/TravelPageLayout";
import TravelStepList from "@features/services/travel/TravelStepList";
import TellUsAboutYourselfStep from "@features/services/travel/TellUsAboutYourselfStep";
import ChoosePackageStep from "@features/services/travel/ChoosePackageStep";
import ParticipantsStep from "@features/services/travel/ParticipantsStep";
import TravelFormNavigation from "@features/services/travel/TravelFormNavigation";
import TravelTrackingView from "@features/services/travel/TravelTrackingView";
import {
  DAYS_OPTIONS,
  HEAR_OPTIONS,
  REASON_OPTIONS,
  STEPS,
  TRACKING_PARAM,
  VIEW_PARAM,
  isValidBirthYear,
} from "@features/services/travel/types";
import type {
  PackageType,
  Participant,
  Step1Data,
} from "@features/services/travel/types";
import { isValidPhone } from "lib/phoneUtils";
import { submitTravelApplicationAction } from "server/actions/travel";
import { useOrgId } from "hooks/useOrgId";

const TravelPage = () => {
  const orgId = useOrgId();
  const searchParams = useSearchParams();
  const router = useRouter();

  const viewParam = searchParams.get(VIEW_PARAM);
  const trackingParam = searchParams.get(TRACKING_PARAM);

  const navigateTo = useCallback(
    (params: Record<string, string>) => {
      const sp = new URLSearchParams(params);
      router.push(`?${sp.toString()}`);
    },
    [router],
  );

  const navigateHome = useCallback(() => {
    router.push("?");
  }, [router]);

  const [step, setStep] = useState(0);
  const [step1SubmitAttempted, setStep1SubmitAttempted] = useState(false);
  const [step1Data, setStep1Data] = useState<Step1Data>({
    name: "",
    phone: "",
    yearOfBirth: "",
    profession: "",
    reasonForTravel: REASON_OPTIONS[0].value,
    howDidYouHear: HEAR_OPTIONS[0].value,
  });
  const [selectedDays, setSelectedDays] = useState(DAYS_OPTIONS[0].value);
  const [selectedPackage, setSelectedPackage] =
    useState<PackageType>("standard");
  const [customServices, setCustomServices] = useState<string[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([
    { id: "1", name: "" },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [submittedRef, setSubmittedRef] = useState<string | null>(null);

  const onGetStarted = useCallback(() => {
    navigateTo({ [VIEW_PARAM]: "form" });
  }, [navigateTo]);

  const onTrackApplication = useCallback(
    (ref: string) => {
      navigateTo({ [VIEW_PARAM]: "track", [TRACKING_PARAM]: ref });
    },
    [navigateTo],
  );

  const updateStep1 = useCallback(
    <K extends keyof Step1Data>(key: K, value: Step1Data[K]) => {
      setStep1Data((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const addParticipant = useCallback(() => {
    setParticipants((prev) => [...prev, { id: String(Date.now()), name: "" }]);
  }, []);

  const updateParticipant = useCallback((id: string, name: string) => {
    setParticipants((prev) =>
      prev.map((p) => (p.id === id ? { ...p, name } : p)),
    );
  }, []);

  const removeParticipant = useCallback((id: string) => {
    setParticipants((prev) =>
      prev.length > 1 ? prev.filter((p) => p.id !== id) : prev,
    );
  }, []);

  const addCustomService = useCallback((service: string) => {
    setCustomServices((prev) =>
      prev.includes(service) ? prev : [...prev, service],
    );
  }, []);

  const removeCustomService = useCallback((service: string) => {
    setCustomServices((prev) => prev.filter((s) => s !== service));
  }, []);

  const canAdvance = useMemo(() => {
    if (step === 0) {
      return (
        step1Data.name.trim() !== "" &&
        isValidPhone(step1Data.phone) &&
        step1Data.yearOfBirth.trim() !== "" &&
        isValidBirthYear(step1Data.yearOfBirth) &&
        step1Data.profession.trim() !== ""
      );
    }
    if (step === 1) {
      if (selectedPackage === "custom") {
        return customServices.length > 0;
      }
      return true;
    }
    if (step === 2) {
      return participants.some((p) => p.name.trim() !== "");
    }
    return true;
  }, [step, step1Data, selectedPackage, customServices, participants]);

  const onStepChange = useCallback(
    (e: { step: number }) => {
      if (e.step > step && !canAdvance) {
        return;
      }
      setStep(e.step);
    },
    [step, canAdvance],
  );

  const onTrackSubmittedApplication = useCallback(() => {
    if (submittedRef != null) {
      navigateTo({
        [VIEW_PARAM]: "track",
        [TRACKING_PARAM]: submittedRef,
      });
    }
  }, [submittedRef, navigateTo]);

  const handleNext = useCallback(() => {
    if (step === 0) {
      setStep1SubmitAttempted(true);
    }
    if (canAdvance) {
      setStep((prev) => prev + 1);
    }
  }, [step, canAdvance]);

  const handleFinish = useCallback(async () => {
    setSubmitting(true);
    try {
      const result = await submitTravelApplicationAction(orgId, {
        name: step1Data.name,
        phone: step1Data.phone,
        yearOfBirth: step1Data.yearOfBirth,
        profession: step1Data.profession,
        reasonForTravel: step1Data.reasonForTravel,
        howDidYouHear: step1Data.howDidYouHear,
        packageType: selectedPackage,
        selectedDays,
        customServices,
        participants: participants
          .map((p) => p.name.trim())
          .filter((n) => n !== ""),
      });
      setSubmittedRef(result.trackingRef);
      setStep(STEPS.length);
    } finally {
      setSubmitting(false);
    }
  }, [orgId, step1Data, selectedDays, selectedPackage, customServices, participants]);

  useEffect(() => {
    if (step === 2 && step1Data.name.trim() !== "") {
      setParticipants((prev) => {
        if (prev[0] && !prev[0].name.trim()) {
          return prev.map((p, i) =>
            i === 0 ? { ...p, name: step1Data.name } : p,
          );
        }
        return prev;
      });
    }
  }, [step, step1Data.name]);

  if (viewParam === "track" && trackingParam != null) {
    return (
      <TravelPageLayout>
        <TravelTrackingView
          trackingRef={trackingParam}
          onBack={navigateHome}
        />
      </TravelPageLayout>
    );
  }

  if (viewParam === "form") {
    return (
      <TravelPageLayout>
        <Steps.Root
          step={step}
          onStepChange={onStepChange}
          count={STEPS.length}
        >
          <TravelStepList />
          <Steps.Content index={0}>
            <TellUsAboutYourselfStep
              data={step1Data}
              onChange={updateStep1}
              showAllErrors={step1SubmitAttempted}
            />
          </Steps.Content>
          <Steps.Content index={1}>
            <ChoosePackageStep
              selectedDays={selectedDays}
              selectedPackage={selectedPackage}
              customServices={customServices}
              onDaysChange={setSelectedDays}
              onPackageSelect={setSelectedPackage}
              onAddCustomService={addCustomService}
              onRemoveCustomService={removeCustomService}
            />
          </Steps.Content>
          <Steps.Content index={2}>
            <ParticipantsStep
              participants={participants}
              onAddParticipant={addParticipant}
              onUpdateParticipant={updateParticipant}
              onDeleteParticipant={removeParticipant}
            />
          </Steps.Content>
          <Steps.CompletedContent>
            <Flex flexDirection="column" align="center" gap={4} mt={8}>
              <Heading size="lg" color="fg">
                All done!
              </Heading>
              <Text color="text.secondary">
                Your travel application has been submitted.
              </Text>
              {submittedRef != null && (
                <>
                  <Box
                    px={4}
                    py={3}
                    borderWidth="1px"
                    borderColor="border.dark"
                    borderRadius="md"
                    bg="bg.muted"
                  >
                    <Text fontSize="sm" color="text.secondary" mb={1}>
                      Your tracking reference
                    </Text>
                    <Text fontSize="lg" fontWeight="bold" color="fg">
                      {submittedRef}
                    </Text>
                  </Box>
                  <Text
                    as="button"
                    fontSize="sm"
                    color="fg.muted"
                    cursor="pointer"
                    textDecoration="underline"
                    onClick={onTrackSubmittedApplication}
                  >
                    Track your application
                  </Text>
                </>
              )}
            </Flex>
          </Steps.CompletedContent>
          <TravelFormNavigation
            step={step}
            canAdvance={canAdvance}
            submitting={submitting}
            onNext={handleNext}
            onFinish={handleFinish}
          />
        </Steps.Root>
      </TravelPageLayout>
    );
  }

  return (
    <TravelLandingView
      onGetStarted={onGetStarted}
      onTrackApplication={onTrackApplication}
    />
  );
};

export default TravelPage;
